import { Link } from "react-router-dom";
import { LogIn, Menu, X, ChevronDown } from "lucide-react";
import { useState, useContext, useEffect } from "react";
import { AuthContext } from "@/App";
import { Button } from "@/components/ui/button";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DEFAULT_CONFIG = {
  logoUrl: "https://customer-assets.emergentagent.com/job_52f1121e-b9de-4fec-a879-a87485a0115a/artifacts/4kxqjmnl_HOKICO%20%289%29.png",
  logoHeight: 48,
  logoHeightTablet: 56,
  logoHeightDesktop: 64,
  navItems: [
    { id: "home", label: "Home", link: "/", order: 0, visibleOnDesktop: true, visibleOnTablet: true, visibleOnMobile: true },
    { id: "about", label: "About Us", link: "/about", order: 1, visibleOnDesktop: true, visibleOnTablet: true, visibleOnMobile: true },
    { id: "manufacturing", label: "Manufacturing", link: "/manufacturing", order: 2, visibleOnDesktop: true, visibleOnTablet: true, visibleOnMobile: true },
    { id: "industries", label: "Industries", link: "/industries", order: 3, visibleOnDesktop: true, visibleOnTablet: true, visibleOnMobile: true },
    { id: "case-studies", label: "Case Studies", link: "/case-studies", order: 4, visibleOnDesktop: true, visibleOnTablet: true, visibleOnMobile: true },
  ],
  desktopVisibleCount: 5,
  tabletVisibleCount: 3,
  mobileVisibleCount: 0,
  loginButtonText: "Client Login",
  showLoginButton: true
};

export default function Navbar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [moreMenuOpen, setMoreMenuOpen] = useState(false);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const { user, logout } = useContext(AuthContext);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const response = await axios.get(`${API}/config/header`);
        setConfig({ ...DEFAULT_CONFIG, ...response.data });
      } catch (error) {
        console.error("Error fetching header config:", error);
      }
    };
    fetchConfig();
  }, []);

  // Sort nav items by order
  const sortedNavItems = [...(config.navItems || [])].sort((a, b) => a.order - b.order);
  
  // Filter items for desktop (visible and not in menu)
  const desktopItems = sortedNavItems.filter(item => item.visibleOnDesktop && !item.showInMenu);
  const desktopVisibleItems = desktopItems.slice(0, config.desktopVisibleCount);
  const desktopMoreItems = desktopItems.slice(config.desktopVisibleCount);
  
  // Filter items for mobile menu
  const mobileItems = sortedNavItems.filter(item => item.visibleOnMobile);

  return (
    <nav className="border-b border-border/50 bg-white/95 backdrop-blur-sm sticky top-0 z-50 shadow-sm">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex items-center justify-between h-20 lg:h-24">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="navbar-logo">
            <img 
              src={config.logoUrl} 
              alt="Hokico Logo" 
              className="w-auto"
              style={{
                height: `${config.logoHeight}px`
              }}
            />
            <style>{`
              @media (min-width: 768px) {
                [data-testid="navbar-logo"] img {
                  height: ${config.logoHeightTablet}px !important;
                }
              }
              @media (min-width: 1024px) {
                [data-testid="navbar-logo"] img {
                  height: ${config.logoHeightDesktop}px !important;
                }
              }
            `}</style>
          </Link>

          {/* Desktop Navigation - Centered (only visible on lg: 1024px and above) */}
          <div className="hidden lg:flex items-center gap-6 xl:gap-8 absolute left-1/2 transform -translate-x-1/2">
            {!user && (
              <>
                {desktopVisibleItems.map((item) => (
                  <Link 
                    key={item.id}
                    to={item.link} 
                    className="text-base xl:text-lg font-medium hover:text-primary transition-colors whitespace-nowrap"
                    data-testid={`nav-${item.id}`}
                  >
                    {item.label}
                  </Link>
                ))}
                
                {/* More Menu Dropdown */}
                {desktopMoreItems.length > 0 && (
                  <div className="relative">
                    <button 
                      onClick={() => setMoreMenuOpen(!moreMenuOpen)}
                      className="text-base xl:text-lg font-medium hover:text-primary transition-colors whitespace-nowrap flex items-center gap-1"
                      data-testid="nav-more-btn"
                    >
                      More
                      <ChevronDown className={`h-4 w-4 transition-transform ${moreMenuOpen ? 'rotate-180' : ''}`} />
                    </button>
                    {moreMenuOpen && (
                      <div 
                        className="absolute top-full left-0 mt-2 bg-white border rounded-lg shadow-lg py-2 min-w-[160px] z-50"
                        onMouseLeave={() => setMoreMenuOpen(false)}
                      >
                        {desktopMoreItems.map((item) => (
                          <Link
                            key={item.id}
                            to={item.link}
                            className="block px-4 py-2 text-sm hover:bg-muted transition-colors"
                            onClick={() => setMoreMenuOpen(false)}
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </>
            )}

            {user && (
              <>
                {user.role === "admin" ? (
                  <Link to="/admin" data-testid="nav-admin-link">
                    <Button variant="ghost">Admin Dashboard</Button>
                  </Link>
                ) : (
                  <>
                    <Link to="/catalog" data-testid="nav-catalog-link">
                      <Button variant="ghost">Catalog</Button>
                    </Link>
                    <Link to="/my-quotes" data-testid="nav-quotes-link">
                      <Button variant="ghost">My Quotes</Button>
                    </Link>
                  </>
                )}
              </>
            )}
          </div>

          {/* Right Side - Login/User (only visible on lg: 1024px and above) */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            {user ? (
              <>
                <span className="text-sm text-muted-foreground">{user.email}</span>
                <Button onClick={logout} variant="outline" size="sm" data-testid="nav-logout-btn">
                  Logout
                </Button>
              </>
            ) : (
              config.showLoginButton && (
                <Link to="/login" data-testid="nav-login-link">
                  <Button className="gap-2" data-testid="nav-login-btn">
                    <LogIn className="h-4 w-4" />
                    {config.loginButtonText}
                  </Button>
                </Link>
              )
            )}
          </div>

          {/* Mobile/Tablet Menu Button (visible on screens below lg: 1024px) */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile/Tablet Menu (visible on screens below lg: 1024px) */}
        {mobileMenuOpen && (
          <div className="lg:hidden py-4 border-t" data-testid="mobile-menu">
            {!user && (
              <>
                {mobileItems.map((item) => (
                  <Link 
                    key={item.id}
                    to={item.link} 
                    className="block py-3 text-base font-medium hover:text-primary" 
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    {item.label}
                  </Link>
                ))}
                {config.showLoginButton && (
                  <div className="pt-4 mt-4 border-t">
                    <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                      <Button className="w-full gap-2">
                        <LogIn className="h-4 w-4" />
                        {config.loginButtonText}
                      </Button>
                    </Link>
                  </div>
                )}
              </>
            )}
            
            {user && (
              <>
                {user.role === "admin" ? (
                  <Link to="/admin" className="block py-3 text-base font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
                    Admin Dashboard
                  </Link>
                ) : (
                  <>
                    <Link to="/catalog" className="block py-3 text-base font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
                      Catalog
                    </Link>
                    <Link to="/my-quotes" className="block py-3 text-base font-medium hover:text-primary" onClick={() => setMobileMenuOpen(false)}>
                      My Quotes
                    </Link>
                  </>
                )}
                <div className="pt-4 mt-4 border-t flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">{user.email}</span>
                  <Button onClick={() => { logout(); setMobileMenuOpen(false); }} variant="outline" size="sm">
                    Logout
                  </Button>
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}
